import { useState } from 'react';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { Modal } from 'us-react-components/dist/Modal/Modal';

import { Button, ButtonType } from '../../../components';

export type CloseJobStatus =
  | 'closed_fulfilled_us'
  | 'closed_fulfilled_nus'
  | 'closed_not_fulfilled'
  | 'closed_invalid';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (status: CloseJobStatus) => Promise<void>;
}

export const CloseJobModal = ({ isOpen, onClose, onSubmit }: Props): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const [selected, setSelected] = useState<CloseJobStatus | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options: Array<{ value: CloseJobStatus; label: string }> = [
    { value: 'closed_fulfilled_us', label: t('pages.jobDetail.closeModal.option1') },
    { value: 'closed_fulfilled_nus', label: t('pages.jobDetail.closeModal.option2') },
    { value: 'closed_not_fulfilled', label: t('pages.jobDetail.closeModal.option3') },
    { value: 'closed_invalid', label: t('pages.jobDetail.closeModal.option4') },
  ];

  const handleSubmit = async (): Promise<void> => {
    if (!selected) {
      setError(t('pages.jobDetail.closeModal.validationError'));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(selected);
    } catch {
      setError(t('pages.jobDetail.closeModal.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (): void => {
    setSelected('');
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('pages.jobDetail.closeModal.title')}
      className='w-full max-w-lg'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={handleSubmit}
          >
            {t('pages.jobDetail.closeModal.submit')}
          </Button>
        </>
      }
    >
      <fieldset className='mb-4'>
        <legend className='mb-2 font-semibold text-sm'>
          {t('pages.jobDetail.closeModal.legend')}
        </legend>
        <div className='flex flex-col gap-2'>
          {options.map((opt) => (
            <label key={opt.value} className='flex items-start gap-2 cursor-pointer'>
              <input
                type='radio'
                name='close-reason'
                value={opt.value}
                checked={selected === opt.value}
                onChange={(): void => setSelected(opt.value)}
                className='mt-1'
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      {error && (
        <div className='mb-3 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-sm'>
          {error}
        </div>
      )}
      <div className='text-sm text-neutral-500 italic mb-4'>
        {t('pages.jobDetail.closeModal.footer')}
      </div>
    </Modal>
  );
};
